/** "End mission": ends the selected mission - final, no reopen - and starts its ePCR (the close-out).
 *  Sentio records it as an extra End Mission event from this page, marked as the page's (never the
 *  device's; sentio missionagent/integration/closeout/ending.py). Anyone who can see the mission may end it.
 *  A mission a simulation is still playing cannot be ended here: stop the simulation first (its Stop leaves the
 *  mission open), then end it.
 *  Once ended (from the device or here) it reads "Mission ended", with when and by whom. */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useCookies } from "react-cookie";

import { endMission, errorMessage, fetchSimulation } from "../api";
import type { CloseoutResponse } from "../types";
import { TOP_CONTROL_HEIGHT } from "./MissionPicker";

const hhmm = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const ACTIVE = ["queued", "running", "stopping"];

type EndMissionButtonProps = {
  deviceId: string;
  /** null while no engine-processed mission is selected. */
  missionId: string | null;
  /** The intervention id, for the dialog (and to tell whether a simulation is playing it). */
  incident: string;
  /** When and by whom the mission was ended (dashboard); null while it is not. */
  endedAt: string | null;
  endedBy: string | null;
  /** Called with the answer once ended: the page re-reads the mission and the ePCR. */
  onEnded: (answer: CloseoutResponse) => void;
};

export function EndMissionButton(props: EndMissionButtonProps) {
  const { deviceId, missionId, incident, endedAt, endedBy, onEnded } = props;
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const [confirming, setConfirming] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ended here, before the dashboard has caught up.
  const [endedHere, setEndedHere] = useState<string | null>(null);

  if (missionId === null || !missionId.startsWith("sentio:")) return null;

  const ended = endedAt ?? (endedHere && missionId === endedHere ? new Date().toISOString() : null);
  if (ended) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
        <Button size="small" variant="outlined" disabled sx={{ height: TOP_CONTROL_HEIGHT }}>
          Mission ended
        </Button>
      </Box>
    );
  }

  const open = async () => {
    setError(null);
    setPlaying(false);
    setConfirming(true);
    if (!token) return;
    try {
      const simulation = (await fetchSimulation(token, deviceId)).simulation;
      setPlaying(!!simulation && ACTIVE.includes(simulation.state) && simulation.intervention_id === incident);
    } catch {
      // Simulation is not available here (production): nothing can be playing.
    }
  };

  const end = async () => {
    if (!token || !missionId) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await endMission(token, missionId);
      setEndedHere(missionId);
      setConfirming(false);
      onEnded(answer);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="error"
        sx={{ height: TOP_CONTROL_HEIGHT }}
        disabled={busy}
        onClick={() => void open()}
      >
        End mission
      </Button>
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="sm">
        <DialogTitle>End mission {incident}?</DialogTitle>
        <DialogContent>
          {playing ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              A simulation is playing this mission. Stop it with Simulate first, then end the mission.
            </Alert>
          ) : null}
          <DialogContentText>
            Ending is final: the mission cannot be reopened. Sentio records an End Mission event from this page, marked
            as yours (not the device&apos;s), the mission is marked complete, and its ePCR is generated.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            Device data that arrives afterwards is still recorded, but the mission stays ended.
          </DialogContentText>
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy || playing} onClick={() => void end()}>
            End mission
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
